import type { ReactNode } from 'react'
import type { ServerSession, SessionUser } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

type AuthStatus = 'loading' | 'authenticated' | 'guest'

type AuthContextValue = {
    status: AuthStatus
    user: SessionUser | null
    setUser: (user: SessionUser | null) => void
    refresh: () => Promise<SessionUser | null>
    signOut: () => Promise<void>
}

interface AuthProviderProps {
    children: ReactNode
    session?: ServerSession
}

const AuthContext = createContext<AuthContextValue | null>(null)

const AuthProvider = ({ children, session }: AuthProviderProps): ReactNode => {
    const [user, setUserState] = useState<SessionUser | null>(session?.user ?? null)
    const [status, setStatus] = useState<AuthStatus>(session ? (session.user ? 'authenticated' : 'guest') : 'loading')

    const setUser = useCallback((next: SessionUser | null) => {
        setUserState(next)
        setStatus(next ? 'authenticated' : 'guest')
    }, [])

    const refresh = useCallback(async (): Promise<SessionUser | null> => {
        try {
            const response = await fetch('/api/auth/me', { headers: { Accept: 'application/json' }, cache: 'no-store' })
            const data = (await response.json().catch(() => null)) as { user?: SessionUser | null } | null
            const next = response.ok ? data?.user ?? null : null

            setUser(next)
            return next
        }
        catch {
            setUser(null)
            return null
        }
    }, [setUser])

    useEffect(() => {
        if (!session) void refresh()
    }, [])

    const sessionUserId = session?.user?.id

    useEffect(() => {
        if (session) setUser(session.user)
    }, [sessionUserId])

    const signOut = useCallback(async (): Promise<void> => {
        try {
            await fetch('/api/auth/logout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}'
            })
        }
        finally {
            setUser(null)
        }
    }, [setUser])

    const value = useMemo<AuthContextValue>(() => ({
        status,
        user,
        setUser,
        refresh,
        signOut
    }), [status, user, setUser, refresh, signOut])

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext)
    if (!context) throw new Error('useAuth must be used inside AuthProvider')

    return context
}

export default AuthProvider
